import React from 'react';
import { TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { BikeRide } from "@/api/users/types";
import { format } from 'date-fns';
import { Bike, Clock, Route, ArrowRight, Leaf } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

interface RidesTabProps {
  isLoading: boolean;
  rides: BikeRide[];
}

const RidesTab: React.FC<RidesTabProps> = ({ isLoading, rides }) => {
  const columns = [
    {
      id: "startTime",
      label: "Date & Time",
    },
    {
      id: "duration",
      label: "Duration",
    },
    {
      id: "userName",
      label: "User",
    },
    {
      id: "bike",
      label: "Bike",
    },
    {
      id: "distance",
      label: "Distance",
    },
    {
      id: "locations",
      label: "Route",
    },
    {
      id: "offset",
      label: "CO2 Offset",
    }
  ];

  const renderCell = (ride: BikeRide, attribute: string) => {
    switch (attribute) {
      case "startTime":
        return (
          <div>
            <div className="font-medium">{format(ride.startTime, 'dd MMM yyyy')}</div>
            <div className="text-xs text-muted-foreground">
              {format(ride.startTime, 'HH:mm')} - {format(ride.endTime, 'HH:mm')}
            </div>
          </div>
        );
      case "userName":
        return (
          <div className="font-medium">{ride.userName}</div>
        );
      case "bike":
        return (
          <div className="flex items-center gap-2">
            <Bike className="h-4 w-4 text-muted-foreground" />
            <div className="flex flex-col">
              <span>{ride.serialNumber || ride.bikeName || 'Unknown Bike'}</span>
              <span className="text-xs text-muted-foreground">{ride.bikeName || 'Unknown Model'}</span>
            </div>
          </div>
        );
      case "distance":
        return (
          <div className="font-medium">{ride.distance.toFixed(2)} km</div>
        );
      case "duration":
        return (
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>{ride.duration} min</span>
          </div>
        );
      case "locations":
        return (
          <div className="flex items-center gap-1">
            <Route className="h-4 w-4 text-muted-foreground" />
            <span>{ride.startLocation} → {ride.endLocation}</span>
          </div>
        );
      case "offset":
        return (
          <div className="flex items-center gap-1">
            {ride.countTowardsOffset && (
              <>
                <Leaf className="h-4 w-4 text-green-600" />
                <span className="text-green-600">{(ride.distance * 0.16272).toFixed(2)} kg</span>
              </>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <TabsContent value="rides" className="space-y-6">
      <div className="w-full overflow-auto">
        <Table>
          <TableCaption>Recent rides data.</TableCaption>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.id}>{column.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center">
                  <div className="flex items-center justify-center py-4">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                    <span className="ml-2">Loading rides...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : rides.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center py-8">
                  <div className="flex flex-col items-center justify-center">
                    <Bike className="h-12 w-12 text-muted-foreground mb-2" />
                    <p className="text-muted-foreground font-medium">No rides found</p>
                    <p className="text-xs text-muted-foreground">There are no rides to display at this time.</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rides.map((ride) => (
                <TableRow key={ride.id} className="hover:bg-muted/50">
                  {columns.map((column) => (
                    <TableCell key={`${ride.id}-${column.id}`}>
                      {renderCell(ride, column.id)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </TabsContent>
  );
};

export default RidesTab;
